import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "@/app/routes";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { useCurrentUser, useLogin } from "@/features/auth/hooks";
import { ApiError } from "@/lib/api-client";

export function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const login = useLogin();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    typeof location.state === "object" && location.state && "from" in location.state
      ? String(location.state.from)
      : ROUTES.admin.root;

  if (currentUser.data) {
    return <Navigate to={from} replace />;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    login.mutate(
      { email, password },
      {
        onSuccess: () => navigate(from, { replace: true }),
        onError: (err) => setError(err instanceof ApiError ? err.message : "Unable to sign in."),
      },
    );
  }

  return (
    <main className="cyber-grid grid min-h-screen place-items-center bg-ink px-4 text-content-primary">
      <CyberPanel className="w-full max-w-md">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-200">Kalaco Admin</p>
        <h1 className="mt-3 font-display text-3xl font-bold uppercase text-white">Sign In</h1>
        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-sm text-content-secondary">Email</span>
            <input
              className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/40 px-3 text-white outline-none transition focus:border-cyan-300"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              autoComplete="email"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm text-content-secondary">Password</span>
            <input
              className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/40 px-3 text-white outline-none transition focus:border-cyan-300"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          {error && <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <Button className="w-full" type="submit" disabled={login.isPending}>
            {login.isPending ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </CyberPanel>
    </main>
  );
}
